/**
 * Insulation Step - Form Component
 *
 * Step 2 of the Energy Certificate Simulator wizard.
 * Collects building insulation information: walls, roof, windows.
 */

import {
  Stack,
  SegmentedControl,
  Text,
  Paper,
  Group,
  ThemeIcon,
  Badge,
  Slider,
  Collapse,
} from '@mantine/core';
import {
  IconWall,
  IconHome2,
  IconWindow,
  IconHammer,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import type { UseFormReturnType } from '@mantine/form';
import type { SimulatorInput, InsulationLevel, WindowType, BuildingPeriod, RenovationStatus } from '../../types';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get suggested insulation level based on building period
 * Maps construction era to typical insulation state
 */
function getSuggestedInsulation(buildingPeriod: BuildingPeriod | undefined): InsulationLevel {
  switch (buildingPeriod) {
    case 'before_1945':
    case '1945_1966':
    case '1967_1985':
      return 'none';
    case '1986_1998':
    case '1999_2008':
      return 'partial';
    case '2009_2013':
    case '2014_2020':
      return 'full';
    case '2021_plus':
      return 'modern';
    default:
      return 'partial';
  }
}

/**
 * Get suggested window type based on building period
 * Maps construction era to typical window type used at that time
 */
function getSuggestedWindowType(buildingPeriod: BuildingPeriod | undefined): WindowType {
  switch (buildingPeriod) {
    case 'before_1945':
      return 'single';
    case '1945_1966':
    case '1967_1985':
    case '1986_1998':
      return 'double_old';
    case '1999_2008':
    case '2009_2013':
    case '2014_2020':
      return 'double_new';
    case '2021_plus':
      return 'triple';
    default:
      return 'double_new';
  }
}

// =============================================================================
// CONSTANTS
// =============================================================================

const INSULATION_LEVELS: { value: InsulationLevel; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'Brak',
    description: 'Brak izolacji termicznej',
  },
  {
    value: 'partial',
    label: 'Częściowa',
    description: 'Izolacja < 10 cm lub niekompletna',
  },
  {
    value: 'full',
    label: 'Pełna',
    description: 'Izolacja 10-15 cm, kompletna',
  },
  {
    value: 'modern',
    label: 'Nowoczesna',
    description: 'Izolacja > 15 cm, wysoka jakość',
  },
];

const RENOVATION_OPTIONS: { value: RenovationStatus; label: string; description: string }[] = [
  { value: 'none', label: 'Nie', description: 'Budynek w stanie oryginalnym' },
  { value: 'partial', label: 'Tak, częściowo', description: 'Np. docieplono ściany, ale nie dach' },
  { value: 'full', label: 'Tak, kompleksowo', description: 'Ściany, dach, podłoga, mostki termiczne' },
];

const WINDOW_TYPES: { value: WindowType; label: string; uValue: string; description: string }[] = [
  { value: 'single', label: 'Pojedyncze', uValue: 'U ≈ 5.0', description: 'Bardzo wysokie straty ciepła, przedwojenne' },
  { value: 'double_old', label: 'Podwójne stare', uValue: 'U ≈ 2.8', description: 'Drewniane ramy, montowane przed 2000' },
  { value: 'double_new', label: 'Podwójne nowe', uValue: 'U ≈ 1.3', description: 'PCV lub aluminium, zespolone szyby' },
  { value: 'triple', label: 'Trzyszybowe', uValue: 'U ≤ 0.9', description: 'Energooszczędne, wymagane od WT 2021' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export interface InsulationStepProps {
  /** Mantine form instance */
  form: UseFormReturnType<SimulatorInput>;
}

/**
 * Insulation data collection step
 *
 * Collects:
 * - Wall insulation level
 * - Roof/attic insulation level
 * - Window type/glazing
 */
export function InsulationStep({ form }: InsulationStepProps) {
  const suggestedInsulation = getSuggestedInsulation(form.values.buildingPeriod);
  const suggestedWindowType = getSuggestedWindowType(form.values.buildingPeriod);

  const showRenovationYear = form.values.renovationStatus !== 'none';

  // Sync form value with visual default when renovation year slider becomes visible.
  // The Slider displays `form.values.renovationYear ?? 2015` but the form value
  // remains undefined until the user interacts, causing validation to fail silently.
  useEffect(() => {
    if (showRenovationYear && form.values.renovationYear == null) {
      form.setFieldValue('renovationYear', 2015);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRenovationYear]);

  return (
    <Stack gap="lg">
      {/* Thermal renovation */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="green">
              <IconHammer size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Termomodernizacja
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Czy budynek był termomodernizowany? Ocieplenie poprawia parametry cieplne.
          </Text>
          <SegmentedControl
            fullWidth
            data={RENOVATION_OPTIONS.map((opt) => ({
              value: opt.value,
              label: (
                <Stack gap={0} align="center">
                  <Text size="sm">{opt.label}</Text>
                  <Text size="xs" c="dimmed">{opt.description}</Text>
                </Stack>
              ),
            }))}
            {...form.getInputProps('renovationStatus')}
            aria-label="Status termomodernizacji"
          />
        </Stack>
      </Paper>

      {/* Renovation year (conditional) */}
      <Collapse in={showRenovationYear}>
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Text fw={500} size="sm">
              Rok termomodernizacji
            </Text>
            <Text size="xs" c="dimmed">
              Nowsze remonty oznaczają lepsze standardy izolacji
            </Text>
            <Slider
              min={1990}
              max={2024}
              step={1}
              marks={[
                { value: 1990, label: '1990' },
                { value: 2000, label: '2000' },
                { value: 2010, label: '2010' },
                { value: 2020, label: '2020' },
                { value: 2024, label: '2024' },
              ]}
              label={(value) => `${value}`}
              thumbLabel="Rok termomodernizacji"
              value={form.values.renovationYear ?? 2015}
              onChange={(value) => form.setFieldValue('renovationYear', value)}
              aria-label="Rok termomodernizacji"
            />
          </Stack>
        </Paper>
      </Collapse>

      {/* Wall insulation */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="blue">
              <IconWall size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Izolacja ścian zewnętrznych
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Stan izolacji termicznej ścian zewnętrznych budynku
          </Text>
          <SegmentedControl
            fullWidth
            orientation="vertical"
            data={INSULATION_LEVELS.map((level) => ({
              value: level.value,
              label: (
                <Group justify="space-between" w="100%" px="xs">
                  <Stack gap={0} align="flex-start">
                    <Group gap="xs">
                      <Text size="sm" ta="left">{level.label}</Text>
                      {level.value === suggestedInsulation && (
                        <Badge size="xs" variant="filled" color="green">
                          Domyślne
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" ta="left">{level.description}</Text>
                  </Stack>
                </Group>
              ),
            }))}
            {...form.getInputProps('wallInsulation')}
            aria-label="Stan izolacji ścian zewnętrznych"
          />
        </Stack>
      </Paper>

      {/* Roof insulation */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="orange">
              <IconHome2 size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Izolacja dachu/stropodachu
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Stan izolacji termicznej dachu lub stropu pod nieogrzewanym poddaszem
          </Text>
          <SegmentedControl
            fullWidth
            orientation="vertical"
            data={INSULATION_LEVELS.map((level) => ({
              value: level.value,
              label: (
                <Group justify="space-between" w="100%" px="xs">
                  <Stack gap={0} align="flex-start">
                    <Group gap="xs">
                      <Text size="sm" ta="left">{level.label}</Text>
                      {level.value === suggestedInsulation && (
                        <Badge size="xs" variant="filled" color="green">
                          Domyślne
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" ta="left">{level.description}</Text>
                  </Stack>
                </Group>
              ),
            }))}
            {...form.getInputProps('roofInsulation')}
            aria-label="Stan izolacji dachu"
          />
        </Stack>
      </Paper>

      {/* Window type */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="cyan">
              <IconWindow size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Typ okien
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Rodzaj oszklenia okien - wpływa znacząco na straty ciepła
          </Text>
          <SegmentedControl
            fullWidth
            orientation="vertical"
            data={WINDOW_TYPES.map((type) => ({
              value: type.value,
              label: (
                <Group justify="space-between" w="100%" px="xs">
                  <Stack gap={0} align="flex-start">
                    <Group gap="xs">
                      <Text size="sm" ta="left">{type.label}</Text>
                      {type.value === suggestedWindowType && (
                        <Badge size="xs" variant="filled" color="green">
                          Domyślne
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" ta="left">{type.description}</Text>
                  </Stack>
                  <Badge size="xs" variant="light" color="gray">
                    {type.uValue}
                  </Badge>
                </Group>
              ),
            }))}
            {...form.getInputProps('windowType')}
            aria-label="Typ okien"
          />
        </Stack>
      </Paper>

      {/* Info box */}
      <Paper p="md" bg="blue.0">
        <Text size="xs" c="blue.8">
          <strong>Wskazówka:</strong> Jeśli nie znasz dokładnego stanu izolacji,
          wybierz opcję najbliższą standardowi dla okresu budowy. Starsze budynki
          (przed 1990) zazwyczaj mają brak lub częściową izolację, nowsze - pełną
          lub nowoczesną.
        </Text>
      </Paper>
    </Stack>
  );
}

export default InsulationStep;
